import { useMutation } from 'react-apollo';
import { DropResult } from 'react-beautiful-dnd';
import {
	AddPobMutation,
	PobNodesQuery,
	SetPobCheckInTypeMutation,
	SetPobNodeMutation,
} from '../graphql/quartermaster-operations';
import { RigQuery } from '../graphql/rig';

export interface UsePobActionsOptions {
	customerID: string;
	transporterID: string;
	rigID: string;
	pobNodeData: any[];
	refetchPobNodes: () => Promise<any>;
}

export interface LoadExpectedPassengersOptions {
	pobNodeIds: string[];
	flightID: string;
}

export interface DepartPassengersOptions {
	pobNodeIds: string[];
}

export function usePobActions(options: UsePobActionsOptions) {
	const [setPobNode] = useMutation(SetPobNodeMutation);
	const [personToNewPobNode, { loading: submittingNewPobNode }] =
		useMutation(AddPobMutation);
	const [loadPassengers, { loading: loadingExpectedPassengers }] = useMutation(
		SetPobCheckInTypeMutation
	);
	const [departPassengersMutation, { loading: loadingDepartingPassengers }] = useMutation(
		SetPobCheckInTypeMutation
	);

	function loadExpectedPassengers(loadOptions: LoadExpectedPassengersOptions) {
		return loadPassengers({
			variables: {
				payload: {
					pobNodeIds: loadOptions.pobNodeIds,
					flightID: loadOptions.flightID,
					rigID: options.rigID,
					newCheckInType: 'ONBOARD',
				},
			},
		}).then(() => options.refetchPobNodes());
	}

	function departPassengers(departOptions: DepartPassengersOptions) {
		return departPassengersMutation({
			variables: {
				payload: {
					pobNodeIds: departOptions.pobNodeIds,
					rigID: options.rigID,
					newCheckInType: 'DEPARTED',
				},
			},
		}).then(() => options.refetchPobNodes());
	}

	function handleOnDragEnd(dropResult: DropResult) {
		if (
			!dropResult.destination ||
			dropResult.source?.droppableId === dropResult.destination.droppableId
		) {
			return;
		}

		const currentPobNode = options.pobNodeData.find(
			(pobNode) => pobNode._id === dropResult.draggableId
		);

		function setCheckInType(checkInType: string) {
			return setPobNode({
				variables: {
					payload: {
						_id: dropResult.draggableId,
						checkInType,
						tpID: options.transporterID,
					},
				},
				optimisticResponse: {
					__typename: 'Mutation',
					setPobNode: {
						__typename: 'PobNode',
						_id: dropResult.draggableId,
						checkInType,
						tpID: options.transporterID,
						lastName: currentPobNode?.lastName,
						firstName: currentPobNode?.firstName,
					},
				},
				update: (cache) => {
					cache.writeQuery({
						query: PobNodesQuery,
						variables: {
							customerID: options.customerID,
							rigID: options.rigID,
						},
						data: {
							GetRigPobNodes: options.pobNodeData.map((pobNode) =>
								pobNode._id === dropResult.draggableId
									? { ...pobNode, checkInType }
									: pobNode
							),
						},
					});
					cache.writeQuery({
						query: RigQuery,
						variables: {
							customerID: options.customerID,
						},
						data: {
							GetPobRigs: {},
						},
					});
				},
			});
		}

		switch (dropResult.destination.droppableId) {
			case 'expected':
				setCheckInType('EXPECTED');
				break;
			case 'onboard':
				setCheckInType('ONBOARD');
				break;
			case 'departing':
				setCheckInType('DEPARTING');
				break;
		}
	}

	function addPob(pobData: any) {
		return personToNewPobNode({
			variables: {
				payload: {
					...pobData,
					lastKnownController: options.rigID.replace('RIG-', ''),
					tpID: options.transporterID,
				},
			},
		});
	}

	return {
		loadExpectedPassengers,
		departPassengers,
		handleOnDragEnd,
		addPob,
		loadingExpectedPassengers,
		loadingDepartingPassengers,
		submittingNewPobNode,
	};
}
